import algoliasearch from 'algoliasearch'

// Directory coachProfiles hits do not include this-week availability.
// Private offerings index that on `availability_windows`; join to the
// directory card by `coach_slug` ↔ `customSlug`. `coach_slug` is searchable
// but not a filterable facet, so we load this-week offering slugs in one
// (paginated) query instead of filtering by each visible coach.
// customSlug is also not searchable or filterable on coachProfiles, so the
// Availability "Available This Week" filter resolves objectIDs from a geo-bounded
// coachProfiles query and ANDs InstantSearch with an objectID OR filter.

const searchClient = algoliasearch(
  process.env.ALGOLIA_APP_ID,
  process.env.ALGOLIA_API_KEY,
)

const HITS_PER_PAGE = 1000
const MAX_PAGES = 20
const DIRECTORY_SEARCH_RADIUS_METERS = 200 * 1609
const CACHE_TTL_MS = 15 * 60 * 1000
const ALGOLIA_FILTER_BUDGET = 1000

const leanSearchParams = {
  attributesToHighlight: [],
  attributesToSnippet: [],
}

export const BOOK_THIS_WEEK_LABEL = 'available this week'
export const BOOK_THIS_WEEK_FILTER_LABEL = 'Available This Week'
export const BOOK_ONLINE_FILTER_LABEL = 'Book on PGA.com'
export const BOOK_THIS_WEEK_PENDING_FILTER = 'objectID:"__book_this_week_pending__"'

export const normalizeCoachDirectorySlug = (value) => (
  String(value || '').trim().toLowerCase()
)

let thisWeekSlugCache = null
const objectIdCaches = new Map()

const isFresh = (entry) => entry && entry.expiresAt > Date.now()

export const resetBookThisWeekCacheForTests = () => {
  thisWeekSlugCache = null
  objectIdCaches.clear()
}

const logBookThisWeekError = (message, error) => {
  // eslint-disable-next-line no-console
  console.error(`[coaches-directory] ${message}`, error)
}

const collectSlugsFromHits = (hits, slugs) => {
  (hits || []).forEach((hit) => {
    const slug = normalizeCoachDirectorySlug(hit?.coach_slug)
    if (slug) slugs.add(slug)
  })
}

const paginatedSearchHits = async (index, searchParams) => {
  const first = await index.search('', { ...searchParams, page: 0 })
  const requestedPages = first.nbPages || 1
  if (requestedPages > MAX_PAGES) {
    logBookThisWeekError('Truncated this-week Algolia pages', {
      nbPages: requestedPages,
      maxPages: MAX_PAGES,
    })
  }
  const pages = [first]
  const nbPages = Math.min(requestedPages, MAX_PAGES)
  if (nbPages > 1) {
    const rest = await Promise.all(
      Array.from({ length: nbPages - 1 }, (_, indexOffset) => (
        index.search('', { ...searchParams, page: indexOffset + 1 })
      )),
    )
    pages.push(...rest)
  }
  return pages.flatMap((result) => result.hits || [])
}

const geoSearchParams = ({ aroundLatLng, aroundRadius } = {}) => ({
  aroundRadius: aroundRadius || DIRECTORY_SEARCH_RADIUS_METERS,
  ...(aroundLatLng
    ? { aroundLatLng }
    : { aroundLatLngViaIP: true }),
})

const fetchThisWeekCoachSlugs = async () => {
  const indexName = process.env.ALGOLIA_PRIVATE_OFFERINGS_INDEX_NAME
  if (!indexName) return new Set()

  const index = searchClient.initIndex(indexName)
  const hits = await paginatedSearchHits(index, {
    filters: 'availability_windows:this_week',
    hitsPerPage: HITS_PER_PAGE,
    attributesToRetrieve: ['coach_slug'],
    responseFields: ['hits', 'nbHits', 'nbPages'],
    ...leanSearchParams,
  })
  const slugs = new Set()
  collectSlugsFromHits(hits, slugs)
  return slugs
}

const sanitizeObjectId = (objectID) => String(objectID || '')
  .replace(/\\/g, '\\\\')
  .replace(/"/g, '\\"')

export const buildBookThisWeekObjectIdFilter = (objectIDs) => {
  const ids = [...new Set((objectIDs || []).map(sanitizeObjectId).filter(Boolean))]
  if (!ids.length) return BOOK_THIS_WEEK_PENDING_FILTER
  if (ids.length > ALGOLIA_FILTER_BUDGET) {
    logBookThisWeekError('Truncated this-week objectID filter', {
      matched: ids.length,
      kept: ALGOLIA_FILTER_BUDGET,
    })
  }
  const limited = ids.slice(0, ALGOLIA_FILTER_BUDGET)
  const inner = limited.map((objectID) => `objectID:"${objectID}"`).join(' OR ')
  return `(${inner})`
}

export const directoryBookThisWeekFilters = ({
  bookThisWeekOnly,
  objectIDs,
  ready,
} = {}) => {
  if (!bookThisWeekOnly) return ''
  if (!ready) return BOOK_THIS_WEEK_PENDING_FILTER
  return buildBookThisWeekObjectIdFilter(objectIDs)
}

const geoCacheKey = ({ aroundLatLng, aroundRadius } = {}) => (
  `${aroundLatLng || 'via-ip'}|${aroundRadius || DIRECTORY_SEARCH_RADIUS_METERS}`
)

const fetchCoachObjectIdsForSlugs = async (slugs, { aroundLatLng, aroundRadius } = {}) => {
  const indexName = process.env.ALGOLIA_COACH_INDEX_NAME
  if (!indexName || !slugs?.size) return []

  const index = searchClient.initIndex(indexName)
  const hits = await paginatedSearchHits(index, {
    hitsPerPage: HITS_PER_PAGE,
    attributesToRetrieve: ['customSlug'],
    responseFields: ['hits', 'nbHits', 'nbPages'],
    getRankingInfo: true,
    ...geoSearchParams({ aroundLatLng, aroundRadius }),
    ...leanSearchParams,
  })

  const matched = []
  hits.forEach((hit) => {
    const slug = normalizeCoachDirectorySlug(hit?.customSlug)
    if (!slug || !slugs.has(slug)) return
    const objectID = sanitizeObjectId(hit.objectID)
    if (!objectID) return
    matched.push({
      objectID,
      distance: hit._rankingInfo?.matchedGeoLocation?.distance ?? Number.POSITIVE_INFINITY,
    })
  })
  matched.sort((a, b) => a.distance - b.distance)

  const objectIDs = []
  const seen = new Set()
  matched.forEach(({ objectID }) => {
    if (seen.has(objectID)) return
    seen.add(objectID)
    objectIDs.push(objectID)
  })
  if (objectIDs.length > ALGOLIA_FILTER_BUDGET) {
    logBookThisWeekError('Truncated this-week objectIDs to those closest to the search center', {
      matched: objectIDs.length,
      kept: ALGOLIA_FILTER_BUDGET,
    })
    return objectIDs.slice(0, ALGOLIA_FILTER_BUDGET)
  }
  return objectIDs
}

export const getBookThisWeekCoachSlugs = () => {
  if (isFresh(thisWeekSlugCache)) {
    return thisWeekSlugCache.promise
  }
  const promise = fetchThisWeekCoachSlugs().catch((error) => {
    thisWeekSlugCache = null
    logBookThisWeekError('Failed loading this-week coach slugs', error)
    throw error
  })
  thisWeekSlugCache = { promise, expiresAt: Date.now() + CACHE_TTL_MS }
  return promise
}

export const getBookThisWeekObjectIds = (geo = {}) => {
  const key = geoCacheKey(geo)
  const cached = objectIdCaches.get(key)
  if (isFresh(cached)) {
    return cached.promise
  }
  const promise = getBookThisWeekCoachSlugs()
    .then((slugs) => fetchCoachObjectIdsForSlugs(slugs, geo))
    .catch((error) => {
      objectIdCaches.delete(key)
      logBookThisWeekError('Failed resolving this-week coach objectIDs', error)
      throw error
    })
  objectIdCaches.set(key, { promise, expiresAt: Date.now() + CACHE_TTL_MS })
  return promise
}

export const coachSlugIsBookableThisWeek = (slug, thisWeekSlugs) => {
  const normalized = normalizeCoachDirectorySlug(slug)
  if (!normalized || !thisWeekSlugs) return false
  return thisWeekSlugs.has(normalized)
}

export const isBookThisWeekParamEnabled = (value) => (
  value === true || value === 'true' || value === '1'
)
